<template>
  <v-dialog
    v-model="modalVisible"
    content-class="v-dialog--full-height v-dialog--with-navigation layout justify-center"
    max-width="22cm"
  >
    <!--<editor-fold desc="Document editor">-->
    <tiny-mce
      v-if="!fileUpload"
      id="document_template_editor"
      key="documentTemplateEditor"
      v-model="documentTemplateDirty.text"
      :insert-content.sync="insertContent"
    />
    <!--</editor-fold>-->

    <!--<editor-fold desc="Drawer">-->
    <aside
      class="elevation-20 v-navigation-drawer v-navigation-drawer--absolute v-navigation-drawer--open v-navigation-drawer--right theme--light"
      data-booted="true"
      style="height: 100%; margin-top: 0px; transform: translateX(0px); "
      :style="{ 'width': navigationDrawerWidth }"
    >
      <div class="pa-4 fill-height overflow-auto">
        <div
          class="d-flex flex-column"
        >
          <div>
            <v-text-field
              v-model="documentTemplateDirty.title"
              v-validate="'required'"
              dense
              name="title"
              label="Template Title"
              placeholder="Enter template title.."
              outlined
              data-vv-as="template title"
              :error-messages="veeErrors.collect('title')"
            />
            <v-textarea
              v-model="documentTemplateDirty.description"
              v-validate="'required'"
              dense
              name="description"
              label="Template Description"
              placeholder="Describe the document purpose for users in business module"
              outlined
              data-vv-as="template description"
              :error-messages="veeErrors.collect('description')"
            />
            <v-switch
              v-model="fileUpload"
              label="PDF Upload"
              dense
              class="mt-0"
            />
            <div class="mb-4">
              <file-upload
                v-if="fileUpload"
                ref="upload"
                v-model="files"
                :post-action="uploadUrl"
                multiple
                :headers="{ 'Authorization': 'Bearer ' + $store.state.auth.token, 'Accept': 'application/json' }"
                @input-file="inputFile"
              >
                <v-btn
                  color="primary"
                  block
                  outlined
                  class="text-none"
                >
                  <v-icon
                    left
                    small
                  >
                    add
                  </v-icon>
                  Upload File (Pdf)
                </v-btn>
              </file-upload>
              <div
                v-for="file in files"
                :key="file.id"
              >
                <v-progress-circular
                  v-if="file.active"
                  color="grey"
                  :size="18"
                  :width="2"
                  indeterminate
                />
                {{ file.name }}
              </div>
              <div v-if="uploadedFile">
                <a
                  href="#"
                  @click.prevent="openFile(uploadedFile.id)"
                >{{ uploadedFile.label }}</a>
              </div>
            </div>
            <v-switch
              v-if="!fileUpload"
              v-model="documentTemplateDirty.editable"
              label="Editable when generating on sales side"
              dense
              class="mt-0"
            />
            <v-autocomplete
              v-model="documentTemplateDirty.countries"
              dense
              :items="countries.data"
              :loading="countries.loading"
              return-object
              item-text="name"
              item-value="id"
              multiple
              small-chips
              deletable-chips
              name="countries"
              style="z-index: 300"
              label="Applicable Countries"
              placeholder="Select countries"
              hint="Leave empty to disable filtering"
              persistent-hint
              autocomplete="disable"
              outlined
              data-vv-as="countries"
            />
            <v-autocomplete
              v-model="documentTemplateDirty.finance_models"
              :items="financeModels.data"
              :loading="financeModels.loading"
              return-object
              multiple
              deletable-chips
              small-chips
              item-text="label"
              item-value="id"
              name="finance_models"
              style="z-index: 300"
              label="Applicable Finance Models"
              placeholder="Select finance models"
              hint="Leave empty to disable filtering"
              persistent-hint
              autocomplete="disable"
              outlined
              data-vv-as="finance models"
            >
              <template v-slot:item="{ item: financeModel }">
                <v-list-item-content>
                  <v-list-item-title>
                    {{ financeModel.label }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    <flag
                      :iso="financeModel.country.code"
                      class="mr-1"
                      :squared="false"
                    />
                    {{ financeModel.country.name }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </template>
              <template v-slot:selection="{ item: financeModel, selected }">
                <v-chip
                  small
                  :selected="selected"
                  close
                  class="v-chip--select-multi"
                  @input="removeFinanceModel(financeModel)"
                >
                  <flag
                    :iso="financeModel.country.code"
                    class="mr-1"
                    :squared="false"
                  />
                  {{ financeModel.label }}
                </v-chip>
              </template>
            </v-autocomplete>
            <v-btn
              color="primary"
              :loading="submitting"
              block
              large
              @click="submit"
            >
              <v-icon
                small
                left
              >
                check
              </v-icon>
              Save
            </v-btn>
            <v-btn
              text
              block
              small
              color="grey"
              @click="modalVisible = false"
            >
              Cancel
            </v-btn>
            <v-btn
              v-if="documentTemplateDirty.id && $can('delete', 'DocumentTemplate')"
              block
              small
              text
              color="error"
              @click="toggleConfirmationDialog(true)"
            >
              <v-icon
                left
                small
              >
                delete
              </v-icon>
              Delete
            </v-btn>
          </div>
          <v-divider class="my-2" />
          <div v-if="!fileUpload">
            <document-template-edit-help @add="addToDocument" />
          </div>
        </div>
      </div>

      <div class="v-navigation-drawer__border" />
    </aside>
    <!--</editor-fold>-->

    <confirm-delete-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      :submitting="deleting"
      text="Are you sure you want to delete this Document Template?"
      @confirmed="deleteDocumentTemplate"
    />
  </v-dialog>
</template>

<script>
  import TinyMce from '@/components/helpers/TinyMce'
  import { cloneDeep } from 'lodash'
  import DocumentTemplate from '@/models/DocumentTemplate'
  import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
  import ConfirmDeleteDialog from '@/components/helpers/ConfirmDeleteDialog'
  import { mapGetters } from 'vuex'
  import FinanceModel from '@/models/FinanceModel'
  import DocumentTemplateEditHelp from '@/components/settings/documentTemplates/DocumentTemplateEditHelp'
  import VueUploadComponent from 'vue-upload-component'
  import File from '@/models/File'

  export default {
    name: 'DocumentTemplateEditDialog',
    components: {
      DocumentTemplateEditHelp,
      TinyMce,
      ConfirmDeleteDialog,
      FileUpload: VueUploadComponent
    },
    mixins: [deleteConfirmationMixin],
    props: {
      visible: {
        type: Boolean,
        default: false
      },
      documentTemplate: {
        type: Object,
        default: () => DocumentTemplate.emptyModel
      }
    },
    data() {
      return {
        documentTemplateDirty: cloneDeep(this.documentTemplate),
        fileUpload: false,
        files: [],
        uploadedFile: null,
        documentTemplateVariablesHelp: [],
        insertContent: null,
        submitting: false,
        financeModels: {
          data: [],
          loading: false
        }
      }
    },
    computed: {
      modalVisible: {
        get: function () {
          return this.visible
        },
        set: function (value) {
          this.$emit('update:visible', value)
        }
      },
      navigationDrawerWidth() {
        return this.$vuetify.breakpoint.xlOnly ? '480px' : '300px'
      },
      ...mapGetters('settings', ['countries'])
    },
    watch: {
      documentTemplate() {
        this.documentTemplateDirty = cloneDeep(this.documentTemplate)

        this.$nextTick(() => {
          if (!this.documentTemplate.text) this.documentTemplateDirty.text = ''
        })
      },
      modalVisible() {
        this.fileUpload = false
        this.files = []
        this.uploadedFile = null

        if (this.documentTemplate.file) {
          this.fileUpload = true
          this.files = []
          this.uploadedFile = this.documentTemplate.file
        }
      }
    },
    created() {
      this.fetchFinanceModels()

      this.uploadUrl = process.env.VUE_APP_API_URL + '/document-templates/upload-file'
    },
    methods: {
      async fetchFinanceModels() {
        this.financeModels.loading = true

        try {
          this.financeModels.data = await FinanceModel.api.getAll()
        } catch (e) {
          throw e
        } finally {
          this.financeModels.loading = false
        }
      },
      async submit() {
        let validation = await this.$validator.validateAll()

        if (!validation) return

        this.save()
      },
      async save() {
        this.submitting = true

        const data = {
          title: this.documentTemplateDirty.title,
          description: this.documentTemplateDirty.description,
          editable: this.uploadedFile ? false : this.documentTemplateDirty.editable,
          type: this.documentTemplateDirty.type,
          text: this.documentTemplateDirty.text,
          countries: this.documentTemplateDirty.countries.map(c => c.id),
          finance_models: this.documentTemplateDirty.finance_models.map(f => f.id),
          file_id: this.uploadedFile ? this.uploadedFile.id : undefined
        }

        try {
          let documentTemplate = await DocumentTemplate.api.save(data, this.documentTemplateDirty.id)

          this.$store.dispatch('alert', {
            'text': 'Document Template Saved',
            'color': 'success'
          })

          this.$emit('documentTemplate:updated', documentTemplate)
          this.modalVisible = false
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
        } finally {
          this.submitting = false
        }
      },
      async deleteDocumentTemplate() {
        this.deleting = true

        try {
          await DocumentTemplate.api.destroy(this.documentTemplateDirty.id)

          this.$store.dispatch('alert', {
            'text': 'Document template deleted',
            'color': 'info'
          })
          this.modalVisible = false
          this.$emit('documentTemplate:deleted', this.documentTemplateDirty.id)
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
          throw e
        } finally {
          this.deleteConfirmationDialogVisible = false
          this.deleting = false
        }
      },
      addToDocument(variable) {
        this.insertContent = variable
      },
      removeFinanceModel(financeModel) {
        this.documentTemplateDirty.finance_models.splice(
          this.documentTemplateDirty.finance_models.findIndex(f => f.id === financeModel.id),
          1
        )
      },
      inputFile(newFile, oldFile) {
        if (newFile && !oldFile) {
          this.$refs.upload.update(newFile, { active: true })
          return
        }

        if (newFile && oldFile && !newFile.active && oldFile.active) {
          // Get response data
          this.$refs.upload.remove(newFile)
          this.uploadedFile = newFile.response
        }
      },
      openFile(fileId) {
        File.api.openDocument(fileId)
      }
    }
  }
</script>
